import React, { useState, useEffect } from 'react';
import { Person, CreatePerson, UpdatePerson, ContactType, EmailDto, PhoneDto, SocialLinkDto, Customer, CreateCustomerPersonDto } from '../types';
import { customersAPI } from '../services/api';

interface PersonFormProps {
  person?: Person;
  initialCustomerId?: number;
  onSubmit: (person: CreatePerson | UpdatePerson) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const PersonForm: React.FC<PersonFormProps> = ({
  person,
  initialCustomerId,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CreatePerson | UpdatePerson>({
    firstName: '',
    lastName: '',
    middleName: '',
    notes: '',
    linkedInUrl: '',
    contactType: ContactType.Cold,
    emails: [],
    phones: [],
    socialLinks: [],
    customers: !person && initialCustomerId ? [{ customerId: initialCustomerId, isFormer: false }] : [],
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [socialLinkInput, setSocialLinkInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');

  // New state for managing customer relationships
  const [selectedCustomer, setSelectedCustomer] = useState<number | ''>('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [workingEmail, setWorkingEmail] = useState('');
  const [isFormer, setIsFormer] = useState(false);

  useEffect(() => {
    if (person) {
      setFormData({
        ...person,
        // Transform CustomerPersonDto[] to CreateCustomerPersonDto[]
        customers: person.customers.map(cp => ({
          customerId: cp.customerId,
          personId: cp.personId,
          isFormer: cp.isFormer,
          jobTitle: cp.jobTitle,
          department: cp.department,
          workingEmail: cp.workingEmail,
        })),
      });
      if (person.needsReview) {
        setJobTitle(person.importJobTitle || '');
        setDepartment(person.importDepartment || '');
        setWorkingEmail(person.importWorkingEmail || '');
      }
    }
  }, [person]);

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const customersData = await customersAPI.getCustomers();
        setCustomers(customersData);
      } catch (error) {
        console.error('Error loading customers:', error);
      }
    };
    
    loadCustomers();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'contactType' ? parseInt(value) as ContactType : value,
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  const validatePhone = (phone: string) => {
    const phoneRegex = /^[0-9+\-() .]{5,32}$/;
    return phoneRegex.test(phone);
  };
  
  const handleArrayFieldAdd = (
    field: 'socialLinks' | 'emails' | 'phones',
    value: string,
    inputSetter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return;
  
    if (field === 'emails' && !validateEmail(trimmedValue)) {
      setErrors(prev => ({ ...prev, emails: 'Invalid email address' }));
      return;
    }
  
    if (field === 'phones' && !validatePhone(trimmedValue)) {
      setErrors(prev => ({ ...prev, phones: 'Invalid phone number' }));
      return;
    }
  
    let newItem: EmailDto | PhoneDto | SocialLinkDto;
    switch (field) {
      case 'emails':
        newItem = { id: 0, address: trimmedValue };
        break;
      case 'phones':
        newItem = { id: 0, number: trimmedValue };
        break;
      case 'socialLinks':
        newItem = { id: 0, url: trimmedValue };
        break;
    }
  
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], newItem],
    }));
  
    setErrors(prev => ({ ...prev, [field]: '' }));
    inputSetter('');
  };

  const handleArrayFieldRemove = (field: 'socialLinks' | 'emails' | 'phones', index: number) => {
    setFormData(prev => {
      const currentArray = prev[field] as (EmailDto | PhoneDto | SocialLinkDto)[];
      return {
        ...prev,
        [field]: currentArray.filter((_, i) => i !== index),
      };
    });
  };

  const handleAddCustomer = () => {
    if (!selectedCustomer) return;

    const newCustomerPerson = {
      customerId: typeof selectedCustomer === 'number' ? selectedCustomer : 0,
      isFormer,
      jobTitle,
      department,
      workingEmail,
    };

    setFormData(prev => ({
      ...prev,
      customers: [...prev.customers, newCustomerPerson],
    }));

    // Reset fields
    setSelectedCustomer('');
    setErrors(prev => ({ ...prev, customers: '' }));
    setIsFormer(false);
    setJobTitle('');
    setDepartment('');
    setWorkingEmail('');
  };

  const handleRemoveCustomer = (index: number) => {
    setFormData(prev => ({
      ...prev,
      customers: prev.customers.filter((_, i) => i !== index),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (person?.needsReview) {
      if (formData.customers.length === 0) newErrors.customers = 'Assign at least one customer to complete review.';
      if (formData.emails.some(email => !validateEmail(email.address.trim()))) newErrors.emails = 'Correct or remove invalid email addresses.';
      if (formData.phones.some(phone => !validatePhone(phone.number.trim()))) newErrors.phones = 'Correct or remove invalid phone numbers.';
      if (formData.socialLinks.some(link => !/^https?:\/\//i.test(link.url.trim()))) newErrors.socialLinks = 'Correct or remove invalid contact links.';
      if (formData.linkedInUrl && !/^https?:\/\//i.test(formData.linkedInUrl.trim())) newErrors.linkedInUrl = 'Use an HTTP(S) LinkedIn URL.';
      if (formData.customers.some(customer => customer.workingEmail && !validateEmail(customer.workingEmail.trim()))) newErrors.customers = 'Correct or remove invalid working email addresses.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const contactTypeOptions = [
    { value: ContactType.Hot, label: 'Hot' },
    { value: ContactType.Cold, label: 'Cold' },
  ];

  const renderArrayField = (
    label: string, 
    field: 'socialLinks' | 'emails' | 'phones', 
    input: string, 
    setInput: React.Dispatch<React.SetStateAction<string>>,
    placeholder: string,
    type: string = 'text'
  ) => (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <div className="input-group mb-2">
        <input
          type={type}
          className="form-control"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          disabled={isLoading}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleArrayFieldAdd(field, input, setInput);
            }
          }}
        />
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => handleArrayFieldAdd(field, input, setInput)}
          disabled={isLoading || !input.trim()}
        >
          Add
        </button>
      </div>
      <div className="d-flex flex-wrap gap-1">
        {formData[field].map((item, index) => {
          let displayText: string;
          switch (field) {
            case 'emails':
              displayText = (item as EmailDto).address;
              break;
            case 'phones':
              displayText = (item as PhoneDto).number;
              break;
            case 'socialLinks':
              displayText = (item as SocialLinkDto).url;
              break;
          }
          return (
            <span key={index} className="badge bg-secondary d-flex align-items-center gap-1">
              {field === 'socialLinks' ? (
                <a 
                  href={(item as SocialLinkDto).url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white text-decoration-none"
                >
                  {displayText}
                </a>
              ) : (
                displayText
              )}
              <button
                type="button"
                className="btn-close btn-close-white"
                style={{ fontSize: '0.7rem' }}
                onClick={() => handleArrayFieldRemove(field, index)}
                disabled={isLoading}
              />
            </span>
          );
        })}
      </div>
      <div className="mb-1 text-danger" style={{ fontSize: '0.875rem' }}>
        {errors[field]}
      </div>
    </div>
  );

  return (
    <div className="card person-form-card">
      <div className="card-header">
        <h5 className="mb-0">
          {person ? `Edit Person: ${person.firstName} ${person.lastName}` : 'Create New Person'}
        </h5>
      </div>
      <div className="card-body">
        {person?.needsReview && (
          <div className="alert alert-warning" role="note">
            <h6>Contact needs review</h6>
            <p>{person.reviewReason}</p>
            {person.importCompanyName && <p>Extracted company: {person.importCompanyName}</p>}
            {person.importCompanyWebsite && <p>Extracted website: {person.importCompanyWebsite}</p>}
            {person.importSourceLabel && <p>Source: {person.importSourceLabel}</p>}
            {person.importSourceUrl && /^https?:\/\//i.test(person.importSourceUrl) && (
              <p><a href={person.importSourceUrl} target="_blank" rel="noopener noreferrer">View source image</a></p>
            )}
            {person.importSourceText && <details><summary>Original source text</summary><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{person.importSourceText}</pre></details>}
            <p className="mb-0">Correct the contact details and assign a customer before completing review.</p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="person-form">
          <div className="form-section">
            <div>
              <h6 className="section-title">Personal Info</h6>
              <div className="section-divider"></div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
              <label htmlFor="firstName" className="form-label">
                First Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
            </div>

              <div className="col-md-6 mb-3">
              <label htmlFor="lastName" className="form-label">
                Last Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
            </div>

            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="middleName" className="form-label">Middle Name</label>
                <input
                  type="text"
                  className="form-control"
                  id="middleName"
                  name="middleName"
                  value={formData.middleName || ''}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="contactType" className="form-label">Contact Type</label>
                <select
                  className="form-select"
                  id="contactType"
                  name="contactType"
                  value={formData.contactType}
                  onChange={handleInputChange}
                  disabled={isLoading}
                >
                  {contactTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div>
              <h6 className="section-title">Contact Info</h6>
              <div className="section-divider"></div>
            </div>
            <div className="row">
              <div className="col-12 mb-3">
                <label htmlFor="linkedInUrl" className="form-label">LinkedIn URL</label>
                <input
                  type="url"
                  className="form-control"
                  id="linkedInUrl"
                  name="linkedInUrl"
                  value={formData.linkedInUrl || ''}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  placeholder="https://linkedin.com/in/username"
                />
                {errors.linkedInUrl && <div className="text-danger">{errors.linkedInUrl}</div>}
              </div>
            </div>
            {renderArrayField('Emails', 'emails', emailInput, setEmailInput, 'Enter email address', 'email')}
            {renderArrayField('Phone Numbers', 'phones', phoneInput, setPhoneInput, 'Enter phone number', 'tel')}
            {renderArrayField('Social Links', 'socialLinks', socialLinkInput, setSocialLinkInput, 'Enter social media URL', 'url')}
          </div>

          <div className="form-section">
            <div>
              <h6 className="section-title">Employers</h6>
              <div className="section-divider"></div>
            </div>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {formData.customers.map((cp, index) => {
                const customer = customers.find(c => c.id === cp.customerId);
                return (
                  <div key={index} className="card p-2 employer-card" style={{ minWidth: '250px' }}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <strong>{customer?.title || 'Unknown'}</strong>
                        {cp.isFormer && <div className="small text-muted">Former employer</div>}
                        <div className="mt-2 mb-2">
                          <label htmlFor={`employer-job-title-${index}`} className="form-label small">
                            Job title at {customer?.title || `customer ${cp.customerId}`}
                          </label>
                          <input
                            id={`employer-job-title-${index}`}
                            type="text"
                            className="form-control form-control-sm"
                            value={cp.jobTitle || ''}
                            onChange={event => {
                              const value = event.target.value;
                              setFormData(previous => ({
                                ...previous,
                                customers: previous.customers.map((assignment, assignmentIndex) =>
                                  assignmentIndex === index ? { ...assignment, jobTitle: value } : assignment),
                              }));
                            }}
                            disabled={isLoading}
                          />
                        </div>
                        {cp.department && <div className="small text-muted">Dept: {cp.department}</div>}
                        {cp.workingEmail && <div className="small text-muted">Email: {cp.workingEmail}</div>}
                      </div>
                      <button
                        type="button"
                        className="btn action-btn action-delete employer-remove"
                        onClick={() => handleRemoveCustomer(index)}
                        disabled={isLoading}
                        title="Remove employer"
                      >
                        <i className="fa-solid fa-trash" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="form-subcard">
              <h6 className="mb-3">Add Employer</h6>
              <div className="row">
                <div className="col-md-6 mb-2">
                  <select
                    aria-label="Customer"
                    className="form-select form-select-sm"
                    value={selectedCustomer}
                    onChange={(e) => setSelectedCustomer(e.target.value ? parseInt(e.target.value) : '')}
                    disabled={isLoading}
                  >
                    <option value="">Select Customer...</option>
                    {customers
                      .filter(c => !c.isArchive)
                      .filter(c => !person?.needsReview || c.isActive !== false)
                      // allow selecting a customer again if all existing relations are marked as former
                      .filter(c => !formData.customers.some(cp => cp.customerId === c.id && !cp.isFormer))
                      .sort((a, b) => a.title.localeCompare(b.title))
                      .map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {customer.title}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="col-md-6 mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Job Title"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="col-md-6 mb-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isFormer"
                      checked={isFormer}
                      onChange={(e) => setIsFormer(e.target.checked)}
                      disabled={isLoading}
                    />
                    <label className="form-check-label" htmlFor="isFormer">
                      Former employer
                    </label>
                  </div>
                </div>
                <div className="col-md-6 mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Department"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="col-md-6 mb-2">
                  <input
                    type="email"
                    className="form-control form-control-sm"
                    placeholder="Working Email"
                    value={workingEmail}
                    onChange={(e) => setWorkingEmail(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-primary mt-2"
                onClick={handleAddCustomer}
                disabled={isLoading || !selectedCustomer}
              >
                Add Employer
              </button>
              {errors.customers && <div className="text-danger" role="alert">{errors.customers}</div>}
            </div>
          </div>

          <div className="form-section">
            <div>
              <h6 className="section-title">Notes</h6>
              <div className="section-divider"></div>
            </div>
            <div className="mb-3">
              <textarea
                className="form-control"
                id="notes"
                name="notes"
                rows={4}
                value={formData.notes || ''}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="d-flex justify-content-start gap-2 form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : person?.needsReview ? 'Save and complete review' : person ? 'Update' : 'Create'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonForm;
